/**
 * URL Parameters Documentation
 *
 * This file documents all URL parameters used in the frontend application.
 * Parameters are categorized by page/view where they are primarily used,
 * or as "global" if they apply across multiple pages.
 */

/**
 * Global Parameters
 * These parameters affect the application state across multiple pages
 */
export const GLOBAL_PARAMETERS = {
  /** Language parameter (fi, en, sv) */
  LANG: 'lang',

  /** Indicates request is from mobile app */
  IS_FROM_APP: 'isFromApp',

  /** Indicates request is from OmaMehiläinen */
  IS_FROM_OM: 'isFromOM',

  /** Source service identifier */
  SOURCE_SERVICE: 'sourceService',

  /** OmaMehiläinen user ID, preserved across navigation */
  OM_UID: 'omUid',

  /** Translation namespace for localization */
  TRANSLATION_NAMESPACE: 'translationNamespace',

  /**
   * Controls landing page behavior
   * Values: 'true', 'private', 'insurance', 'occupational', 'dental', 'call'
   */
  NO_LANDING: 'noLanding',

  /** Parse URL parameters for redirection */
  PARSE_URL_PARAMS: 'parseurlParams',
} as const

/**
 * Marketing and Analytics Parameters
 * These parameters are used for tracking marketing campaigns and analytics
 */
export const MARKETING_PARAMETERS = {
  /** Google Click ID for tracking Google Ads conversions */
  GCLID: 'gclid',

  /** Google Display Click ID for tracking display ad clicks */
  DCLID: 'dclid',

  /** UTM source parameter - identifies which site sent the traffic */
  UTM_SOURCE: 'utm_source',

  /** UTM medium parameter - identifies marketing medium e.g., banner, email */
  UTM_MEDIUM: 'utm_medium',

  /** UTM campaign parameter - identifies specific campaign */
  UTM_CAMPAIGN: 'utm_campaign',

  /** UTM content parameter - identifies which piece of content led to the click */
  UTM_CONTENT: 'utm_content',

  /** UTM term parameter - identifies search terms used */
  UTM_TERM: 'utm_term',

  /** UTM ID parameter - unique identifier for campaign */
  UTM_ID: 'utm_id',
} as const

/**
 * Search View Parameters
 * Used in the main search view for practitioners/appointments
 */
export const SEARCH_PARAMETERS = {
  /** Node ID for search */
  NODE_ID: 's',

  /** Practitioner ID for search */
  PRACTITIONER_ID: 'p',

  /** Location identifier */
  LOCATION: 'lo',

  /** Date parameter */
  DATE: 'd',

  /** Service modal open flag */
  SERVICE_SELECT: 'serviceSelect',

  /** Disable service selection */
  NO_SERVICE_SELECT: 'noServiceSelect',

  /** Modal to open */
  MODAL: 'modal',

  /** Action for the modal */
  MODAL_ACTION: 'modalAction',

  /** Reason parameter */
  REASON: 'so',

  /** Appointment type filters (e.g., ty[0]=video, ty[1]=phone) */
  APPOINTMENT_TYPE: 'ty',

  /** Time range parameter (e.g. tr[start]=10&tr[end]=12) */
  TIME_RANGE: 'tr',

  /** Patient age group filters (e.g., pag[0]=0-10, pag[1]=11-20) */
  PATIENT_AGE_GROUP: 'ag',

  /** Patient age group filters (e.g., pag[0]=0-10, pag[1]=11-20) */
  PATIENT_AGE_GROUP_OLD: 'pag',

  /** Gender filters (e.g., g[0]=M, g[1]=F) */
  GENDER: 'g',

  LANGUAGE: 'la',

  /** Insurance identifier */
  INSURANCE: 'in',

  /** Insurance payer identifier */
  INSURANCE_PAYER: 'inp',
} as const

/**
 * Appointment Parameters
 * Used in appointment selection and booking flows
 */
export const APPOINTMENT_PARAMETERS = {
  /** Appointment length/duration */
  LENGTH: 'length',

  /** Appointment type */
  TYPE: 'type',

  /** Service ID */
  SERVICE: 'service',

  /** Specialty ID */
  SPECIALTY: 'specialty',

  /** Node ID for appointment */
  NODE: 'node',

  /** Connection ID for appointment */
  CONNECTION_ID: 'conn',

  /** Appointment mode, can be "callback" */
  MODE: 'mode',

  /** Appointment mode, can be "callback" */
  MODE_OLD: 'mo',

  /** Opens callback modal */
  CALLBACK_OPEN: 'callbackOpen',
} as const

/**
 * Insurance Redirect Parameters
 * Used in the insurance redirect flow
 */
export const INSURANCE_PARAMETERS = {
  /**
   * Base64-encoded insurance data containing multiple fields:
   * - userOmUid: OM user ID
   * - appointmentId: Appointment ID
   * - appointmentType: Type of appointment
   * - appointmentLength: Duration of appointment
   * - language: Language setting
   * - insuranceContractId: Insurance contract ID
   * - insurancePayerId: Insurance payer ID
   * - nodeId: Optional node ID
   * - serviceId: Optional service ID
   * - isFromApp: Flag indicating if from app
   */
  DATA: 'data',
} as const

export const OTHER_PARAMETERS = {
  /** Modal shown on Node redirects */
  REDIRECT_MODAL: 'modal',
} as const

export const COOKIE_PARAMETERS = {
  MOBILE: 'MOBILE',
} as const

export const SESSION_STORAGE_KEYS = {
  LANDING_MODAL_SHOWN: 'landingModalShown',
} as const

/**
 * External URL Parameters
 * Parameters that are preserved when linking to external systems
 */
export const EXTERNAL_PARAMETERS = {
  /** Marketing campaign code */
  MARKETING_CAMPAIGN_CODE: 'marketingCampaignCode',
  /** Controls landing page behavior */
  NO_LANDING: GLOBAL_PARAMETERS.NO_LANDING,
  /** Parse URL parameters for redirection */
  PARSE_URL_PARAMS: GLOBAL_PARAMETERS.PARSE_URL_PARAMS,
  /** Source service identifier */
  SOURCE_SERVICE: GLOBAL_PARAMETERS.SOURCE_SERVICE,
} as const

/**
 * Type definitions for parameter values
 */
export type GlobalParameterKeys = keyof typeof GLOBAL_PARAMETERS
export type MarketingParameterKeys = keyof typeof MARKETING_PARAMETERS
export type SearchParameterKeys = keyof typeof SEARCH_PARAMETERS
export type AppointmentParameterKeys = keyof typeof APPOINTMENT_PARAMETERS
export type InsuranceParameterKeys = keyof typeof INSURANCE_PARAMETERS
export type OtherParameterKeys = keyof typeof OTHER_PARAMETERS
export type ExternalParameterKeys = keyof typeof EXTERNAL_PARAMETERS
export type CookieParameterKeys = keyof typeof COOKIE_PARAMETERS

/**
 * All parameter keys
 */
export const ALL_PARAMETERS = {
  ...GLOBAL_PARAMETERS,
  ...MARKETING_PARAMETERS,
  ...SEARCH_PARAMETERS,
  ...APPOINTMENT_PARAMETERS,
  ...INSURANCE_PARAMETERS,
  ...OTHER_PARAMETERS,
  ...EXTERNAL_PARAMETERS,
} as const

export type ParameterKey = keyof typeof ALL_PARAMETERS
